import React, { useState } from "react";
import DisplayUpdateTime from "ui/DisplayUpdateTime";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Pencil, Plus, Trash2, Save } from "lucide-react";
import useSWRMutation from "swr/mutation";
import { putFetcher } from "api/swr";
import { mutate } from "swr";
import { toast } from "@/components/ui/use-toast";

// 渐变预览组件
function GradientPreview({ colors }: { colors: any[] }) {
  if (!colors || colors.length === 0) {
    return <div className="w-full h-full bg-muted rounded" />;
  }

  const firstColor = colors[0];
  const light = firstColor?.light || "#ffffff";
  const dark = firstColor?.dark || "#000000";

  return (
    <div
      className="w-full h-full rounded"
      style={{
        background: `linear-gradient(to right, ${light} 0%, ${dark} 100%)`,
      }}
    />
  );
}

// 生成随机 ID
function generateId(): string {
  return Math.random().toString(36).substring(2, 10);
}

// 编辑对话框组件
function ToningEditDialog({ toning }: { toning: any }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState(toning.name || "");
  const [pro, setPro] = useState(toning.pro || 0);
  const [colors, setColors] = useState<any[]>(toning.colors || []);

  const { trigger: updateToning, isMutating } = useSWRMutation(
    `/spine-tonings/${toning.id}`,
    putFetcher,
    {
      onSuccess: () => {
        toast({
          title: "保存成功",
          description: "调色方案已更新",
          className: "bg-green-500 text-white",
        });
        mutate(["/spine-tonings"]);
        setOpen(false);
      },
      onError: (error) => {
        toast({
          variant: "destructive",
          title: "保存失败",
          description: error.message || "未知错误",
        });
      },
    }
  );

  const handleSave = () => {
    updateToning({ name, pro, colors });
  };

  const handleAddColor = () => {
    setColors([
      ...colors,
      { id: generateId(), light: "#ffffff", dark: "#000000" },
    ]);
  };

  const handleRemoveColor = (index: number) => {
    setColors(colors.filter((_, i) => i !== index));
  };

  const handleColorChange = (
    index: number,
    field: "light" | "dark",
    value: string
  ) => {
    const newColors = [...colors];
    newColors[index] = { ...newColors[index], [field]: value };
    setColors(newColors);
  };

  // 重置表单数据
  const handleOpenChange = (isOpen: boolean) => {
    if (isOpen) {
      setName(toning.name || "");
      setPro(toning.pro || 0);
      setColors(toning.colors || []);
    }
    setOpen(isOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="sm" className="h-7 px-2">
          <Pencil className="h-3.5 w-3.5" />
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>编辑调色方案</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          {/* 基本信息 */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>名称</Label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="调色方案名称"
              />
            </div>
            <div className="space-y-2">
              <Label>价格</Label>
              <Input
                type="number"
                value={pro}
                onChange={(e) => setPro(Number(e.target.value))}
              />
            </div>
          </div>

          {/* 配色列表 */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>配色 ({colors.length})</Label>
              <Button variant="outline" size="sm" onClick={handleAddColor}>
                <Plus className="h-3.5 w-3.5 mr-1" />
                添加配色
              </Button>
            </div>
            <ScrollArea className="h-[200px] border rounded-lg p-2">
              <div className="space-y-2">
                {colors.map((color, index) => (
                  <div
                    key={color.id || index}
                    className="flex items-center gap-2 p-2 border rounded"
                  >
                    {/* 预览 */}
                    <div
                      className="w-16 h-8 rounded border"
                      style={{
                        background: `linear-gradient(to right, ${color.light || "#fff"} 0%, ${color.dark || "#000"} 100%)`,
                      }}
                    />
                    {/* Light 颜色 */}
                    <div className="flex items-center gap-1">
                      <input
                        type="color"
                        value={color.light || "#ffffff"}
                        onChange={(e) =>
                          handleColorChange(index, "light", e.target.value)
                        }
                        className="w-8 h-8 cursor-pointer"
                      />
                      <Input
                        value={color.light || "#ffffff"}
                        onChange={(e) =>
                          handleColorChange(index, "light", e.target.value)
                        }
                        className="w-20 h-8 text-xs font-mono"
                      />
                    </div>
                    {/* Dark 颜色 */}
                    <div className="flex items-center gap-1">
                      <input
                        type="color"
                        value={color.dark || "#000000"}
                        onChange={(e) =>
                          handleColorChange(index, "dark", e.target.value)
                        }
                        className="w-8 h-8 cursor-pointer"
                      />
                      <Input
                        value={color.dark || "#000000"}
                        onChange={(e) =>
                          handleColorChange(index, "dark", e.target.value)
                        }
                        className="w-20 h-8 text-xs font-mono"
                      />
                    </div>
                    {/* 删除按钮 */}
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0 text-destructive"
                      onClick={() => handleRemoveColor(index)}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                ))}
                {colors.length === 0 && (
                  <div className="text-center text-muted-foreground text-sm py-4">
                    暂无配色，点击上方按钮添加
                  </div>
                )}
              </div>
            </ScrollArea>
          </div>

          {/* 保存按钮 */}
          <Button
            onClick={handleSave}
            disabled={isMutating}
            className="w-full"
          >
            <Save className="h-4 w-4 mr-2" />
            {isMutating ? "保存中..." : "保存"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export const columns = [
  {
    header: "#",
    cell: ({ row }: { row: any }) => row.index + 1,
  },
  {
    header: "预览",
    cell: ({ row }: { row: any }) => (
      <div className="w-16 h-6 border rounded overflow-hidden">
        <GradientPreview colors={row.original.colors} />
      </div>
    ),
  },
  {
    header: "ID",
    accessorKey: "id",
    cell: ({ row }: { row: any }) => (
      <span className="font-mono text-xs">{row.original.id}</span>
    ),
  },
  {
    header: "名称",
    accessorKey: "name",
  },
  {
    header: "配色数",
    cell: ({ row }: { row: any }) => (
      <Badge variant="secondary">
        {row.original.colors?.length || 0}
      </Badge>
    ),
  },
  {
    header: "价格",
    accessorKey: "pro",
    cell: ({ row }: { row: any }) => (
      <span>{row.original.pro || 0}</span>
    ),
  },
  {
    header: "更新时间",
    accessorKey: "updateTime",
    cell: ({ row }: { row: any }) => (
      <DisplayUpdateTime updateTime={row.original.updateTime} />
    ),
  },
  {
    header: "操作",
    cell: ({ row }: { row: any }) => <ToningEditDialog toning={row.original} />,
  },
];
